import { useState, useMemo, useEffect } from 'react';
import { useGoals, useGoalMutations } from '../../hooks/useGoals';
import { useBrgList } from '../../hooks/useBrgList';
import { usePillars } from '../../hooks/usePillars';
import { useGoalStatuses } from '../../hooks/useGoalStatuses';
import { useFiscalYears } from '../../hooks/useFiscalYears';
import { useAuth } from '../../auth/useAuth';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { FilterSelect } from '../../components/shared/FilterSelect';
import {
  BrgGoalDto,
  CreateGoalRequest,
  UpdateGoalRequest,
  GoalPillar,
  GoalStatus,
} from '../../types';
import { FiscalYearDto } from '../../api/fiscalYears';



interface AddGoalFormState {
  brgId: string;
  goalText: string;
  keyResultText: string;
  pillar: GoalPillar | '';
  fiscalYear: string;
}

interface EditGoalFormState extends AddGoalFormState {
  goalId: string;
  status: GoalStatus;
  pillar: GoalPillar; // Override to require valid pillar in edit mode
}

export function OkrsPage() {
  const auth = useAuth();
  const isAdmin = auth.roles.includes('BRG.Admin');
  const isLeader = auth.roles.includes('BRG-Lead');

  // Redirect non-Admin/non-Leader users
  if (!isAdmin && !isLeader) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '16px',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <div style={{ fontSize: '4rem', lineHeight: 1 }}>&#128683;</div>
        <h1 style={{ fontSize: '1.6rem', color: '#d32f2f', margin: 0 }}>Access Denied</h1>
        <p style={{ color: '#666', maxWidth: '400px' }}>
          You do not have permission to access this page. Only Admins and Leaders can manage OKRs
          and Smart Goals.
        </p>
      </div>
    );
  }

  const brgListQuery = useBrgList();
  const pillarsQuery = usePillars();
  const goalStatusesQuery = useGoalStatuses();
  const fiscalYearsQuery = useFiscalYears();
  const [filterBrgId, setFilterBrgId] = useState<string>('');
  const [filterFiscalYear, setFilterFiscalYear] = useState<string>('');
  const [filterPillar, setFilterPillar] = useState<string>('');
  const [leaderFilterInitialized, setLeaderFilterInitialized] = useState(false);

  const goalsQuery = useGoals({
    brgId: filterBrgId || undefined,
    fiscalYear: filterFiscalYear || undefined,
    pillar: filterPillar || undefined,
  });

  const goalsMutations = useGoalMutations();

  // Create dynamic pillar list and colors from API data
  const pillarList = useMemo(() => {
    if (!pillarsQuery.data) return [];
    // Filter to only active pillars and return sorted by pillarId
    return pillarsQuery.data
      .filter((p) => p.isActive)
      .sort((a, b) => a.pillarId - b.pillarId);
  }, [pillarsQuery.data]);

  const pillarColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (pillarsQuery.data) {
      pillarsQuery.data.forEach((p) => {
        map[p.pillarName] = p.colorCode;
      });
    }
    return map;
  }, [pillarsQuery.data]);

  // Create dynamic goal status colors from API data
  const statusColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (goalStatusesQuery.data) {
      goalStatusesQuery.data.forEach((status) => {
        map[status.statusName] = status.backgroundColor;
      });
    }
    return map;
  }, [goalStatusesQuery.data]);

  const statusTextColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (goalStatusesQuery.data) {
      goalStatusesQuery.data.forEach((status) => {
        map[status.statusName] = status.textColor;
      });
    }
    return map;
  }, [goalStatusesQuery.data]);

  // Create fiscal year list from API data
  const fiscalYearList = useMemo(() => {
    if (!fiscalYearsQuery.data) return [];
    return fiscalYearsQuery.data.filter((fy) => fy.isActive);
  }, [fiscalYearsQuery.data]);

  // Default to current fiscal year (FY + current year), fallback to first available
  const defaultFiscalYear = useMemo(() => {
    const currentYear = new Date().getFullYear();
    // Try current year first (e.g., "FY 2026")
    let match = fiscalYearList.find(fy => fy.fiscalYearCode === `FY ${currentYear}`);
    if (match) return match.fiscalYearCode;
    
    // Try next year (for fiscal years that run July-June, in August we'd be in FY next year)
    match = fiscalYearList.find(fy => fy.fiscalYearCode === `FY ${currentYear + 1}`);
    if (match) return match.fiscalYearCode;
    
    // Fallback to first available
    return fiscalYearList.length > 0 ? fiscalYearList[0].fiscalYearCode : '';
  }, [fiscalYearList]);

  // UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successBanner, setSuccessBanner] = useState('');
  const [addFormFields, setAddFormFields] = useState<AddGoalFormState[]>([
    {
      brgId: isLeader ? '' : '',
      goalText: '',
      keyResultText: '',
      pillar: '',
      fiscalYear: '',
    },
  ]);
  const [editingGoal, setEditingGoal] = useState<EditGoalFormState | null>(null);
  const [statusDropdownOpenId, setStatusDropdownOpenId] = useState<string | null>(null);

  // Initialize filter fiscal year and form fields with default fiscal year
  useEffect(() => {
    if (defaultFiscalYear && !filterFiscalYear) {
      setFilterFiscalYear(defaultFiscalYear);
    }
  }, [defaultFiscalYear, filterFiscalYear]);

  // Update form fields with default fiscal year
  useEffect(() => {
    if (defaultFiscalYear && addFormFields[0].fiscalYear === '') {
      setAddFormFields([
        {
          brgId: isLeader ? '' : '',
          goalText: '',
          keyResultText: '',
          pillar: '',
          fiscalYear: defaultFiscalYear,
        },
      ]);
    }
  }, [defaultFiscalYear, isLeader]);

  // Initialize Leader's assigned BRGs filter
  // Note: We do NOT pre-select a specific BRG for leaders
  // Instead, we let them default to "All BRGs" (filterBrgId = '')
  useEffect(() => {
    if (isLeader && !leaderFilterInitialized && brgListQuery.data) {
      // Initialize flag when data is loaded - but don't pre-select a BRG
      setLeaderFilterInitialized(true);
    }
  }, [isLeader, brgListQuery.data, leaderFilterInitialized]);

  // Get BRGs available to the current user
  const availableBrgs = useMemo(() => {
    if (!brgListQuery.data) return [];
    if (isAdmin) return brgListQuery.data;
    // For leader, only show their assigned BRGs
    return brgListQuery.data;
  }, [brgListQuery.data, isAdmin]);

  // Group goals by pillar
  const goalsByPillar = useMemo((): Record<string, BrgGoalDto[]> => {
    const grouped: Record<string, BrgGoalDto[]> = {};
    
    // Initialize groups for all active pillars
    if (pillarList && pillarList.length > 0) {
      pillarList.forEach((pillar) => {
        grouped[pillar.pillarName] = [];
      });
    }

    if (!goalsQuery.data) return grouped;

    goalsQuery.data.forEach((goal) => {
      if (grouped[goal.pillar]) {
        grouped[goal.pillar].push(goal);
      }
    });

    // Sort by creation date (newest first), then by goal ID (ascending) for stable sorting
    Object.keys(grouped).forEach((pillar) => {
      grouped[pillar].sort((a, b) => {
        const dateCompare = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        if (dateCompare !== 0) return dateCompare;
        return parseInt(a.goalId) - parseInt(b.goalId);
      });
    });

    return grouped;
  }, [goalsQuery.data, pillarList]);

  // Calculate pillar counts for pie chart
  const pillarCounts = useMemo(
    (): Record<string, number> => {
      const counts: Record<string, number> = {};
      if (pillarList && pillarList.length > 0) {
        pillarList.forEach((pillar) => {
          counts[pillar.pillarName] = goalsByPillar[pillar.pillarName]?.length || 0;
        });
      }
      return counts;
    },
    [goalsByPillar, pillarList]
  );

  // Check if showing empty state
  const isEmptyState = goalsQuery.data && goalsQuery.data.length === 0;

  const handleOpenAddModal = () => {
    setAddFormFields([
      {
        brgId: isLeader && availableBrgs[0] ? availableBrgs[0].brgId.toString() : '',
        goalText: '',
        keyResultText: '',
        pillar: '',
        fiscalYear: defaultFiscalYear,
      },
    ]);
    setShowAddModal(true);
  };

  const handleAddAnother = () => {
    setAddFormFields([
      ...addFormFields,
      {
        brgId: isLeader && availableBrgs[0] ? availableBrgs[0].brgId.toString() : '',
        goalText: '',
        keyResultText: '',
        pillar: '',
        fiscalYear: defaultFiscalYear,
      },
    ]);
  };

  const handleAddFieldChange = (
    index: number,
    field: keyof AddGoalFormState,
    value: string
  ) => {
    const updated = [...addFormFields];
    updated[index] = { ...updated[index], [field]: value };
    setAddFormFields(updated);
  };

  const handleRemoveField = (index: number) => {
    setAddFormFields(addFormFields.filter((_, i) => i !== index));
  };

  const handleAddGoals = async () => {
    // Validate all fields
    const isValid = addFormFields.every(
      (f) => f.brgId && f.goalText && f.keyResultText && f.pillar && f.fiscalYear
    );

    if (!isValid) {
      alert('Please fill in all required fields for all goal/key result pairs');
      return;
    }

    const requests: CreateGoalRequest[] = addFormFields.map((f) => ({
      brgId: f.brgId,
      goalText: f.goalText,
      keyResultText: f.keyResultText,
      pillar: f.pillar as GoalPillar,
      fiscalYear: f.fiscalYear,
    }));

    goalsMutations.createMultiple.mutate(requests, {
      onSuccess: () => {
        setShowAddModal(false);
        setSuccessBanner('Goal(s) was successfully added.');
        setTimeout(() => setSuccessBanner(''), 5000);
      },
      onError: () => {
        alert('Failed to add goals. Please try again.');
      },
    });
  };

  const handleOpenEditModal = (goal: BrgGoalDto) => {
    setEditingGoal({
      goalId: goal.goalId,
      brgId: goal.brgId,
      goalText: goal.goalText,
      keyResultText: goal.keyResultText,
      pillar: goal.pillar,
      fiscalYear: goal.fiscalYear,
      status: goal.status,
    });
    setShowEditModal(true);
  };

  const handleEditFieldChange = (field: keyof EditGoalFormState, value: string) => {
    if (editingGoal) {
      setEditingGoal({ ...editingGoal, [field]: value });
    }
  };

  const handleUpdateGoal = async () => {
    if (!editingGoal) return;

    const updateData: UpdateGoalRequest = {
      goalText: editingGoal.goalText,
      keyResultText: editingGoal.keyResultText,
      pillar: editingGoal.pillar,
      fiscalYear: editingGoal.fiscalYear,
      status: editingGoal.status,
    };

    goalsMutations.update.mutate(
      { id: editingGoal.goalId, data: updateData },
      {
        onSuccess: () => {
          setShowEditModal(false);
          setEditingGoal(null);
          setSuccessBanner('Goal was successfully updated.');
          setTimeout(() => setSuccessBanner(''), 5000);
        },
        onError: () => {
          alert('Failed to update goal. Please try again.');
        },
      }
    );
  };

  const handleStatusChange = (goalId: string, newStatus: GoalStatus) => {
    goalsMutations.updateStatus.mutate(
      { id: goalId, data: { status: newStatus } },
      {
        onSuccess: () => {
          setStatusDropdownOpenId(null);
        },
        onError: () => {
          alert('Failed to update status. Please try again.');
        },
      }
    );
  };

  const handleDeleteGoal = async () => {
    if (!editingGoal) return;

    goalsMutations.delete.mutate(editingGoal.goalId, {
      onSuccess: () => {
        setShowEditModal(false);
        setShowDeleteConfirm(false);
        setEditingGoal(null);
        setSuccessBanner('Goal was successfully deleted.');
        setTimeout(() => setSuccessBanner(''), 5000);
      },
      onError: () => {
        alert('Failed to delete goal. Please try again.');
      },
    });
  };

  const handleClearFilters = () => {
    setFilterBrgId('');
    setFilterFiscalYear(defaultFiscalYear);
    setFilterPillar('');
  };

  const isLoading =
    brgListQuery.isLoading ||
    goalsQuery.isLoading ||
    goalsMutations.create.isPending ||
    goalsMutations.createMultiple.isPending ||
    goalsMutations.update.isPending ||
    goalsMutations.updateStatus.isPending ||
    goalsMutations.delete.isPending;

  const hasError = brgListQuery.isError || goalsQuery.isError;
  const errorMessage =
    brgListQuery.error?.message ?? goalsQuery.error?.message ?? 'Failed to load data.';

  if (isLoading && goalsQuery.data === undefined) {
    return <LoadingSpinner message="Loading OKRs and Smart Goals..." fullPage />;
  }

  if (hasError) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorMessage
          message={errorMessage}
          onRetry={() => {
            void brgListQuery.refetch();
            void goalsQuery.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/* Success Banner */}
      {successBanner && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 700,
            zIndex: 99999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            background: '#d4edda',
            color: '#1a7c3a',
            border: '1px solid #a3d4b0',
            whiteSpace: 'nowrap',
          }}
        >
          ✔ {successBanner}
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '6px',
          padding: '14px 18px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ fontSize: '12px', fontWeight: 700, color: '#444' }}>BRG:</label>
        <FilterSelect
          value={filterBrgId}
          onChange={(e) => setFilterBrgId(e.target.value)}
        >
          <option value="">All BRGs</option>
          {availableBrgs.map((brg) => (
            <option key={brg.brgId} value={brg.brgId.toString()}>
              {brg.name}
            </option>
          ))}
        </FilterSelect>

        <div style={{ width: '1px', height: '24px', background: '#ddd' }} />

        <label style={{ fontSize: '12px', fontWeight: 700, color: '#444' }}>Fiscal Year:</label>
        <FilterSelect
          value={filterFiscalYear}
          onChange={(e) => setFilterFiscalYear(e.target.value)}
        >
          {fiscalYearList.map((fy) => (
            <option key={fy.fiscalYearId} value={fy.fiscalYearCode}>
              {fy.fiscalYearCode}
            </option>
          ))}
        </FilterSelect>

        <div style={{ width: '1px', height: '24px', background: '#ddd' }} />

        <label style={{ fontSize: '12px', fontWeight: 700, color: '#444' }}>Pillar:</label>
        <FilterSelect
          value={filterPillar}
          onChange={(e) => setFilterPillar(e.target.value)}
        >
          <option value="">All Pillars</option>
          {pillarList.map((pillar) => (
            <option key={pillar.pillarName} value={pillar.pillarName}>
              {pillar.pillarName}
            </option>
          ))}
        </FilterSelect>

        <button
          onClick={handleOpenAddModal}
          style={{
            marginLeft: 'auto',
            background: '#1a3bcc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '7px 16px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          + Add Goal / Key Result
        </button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        {/* Pie Chart Panel */}
        <div
          style={{
            width: '260px',
            flexShrink: 0,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '16px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#111', marginBottom: '14px' }}>
            Goals by Pillar
          </div>

          {/* Pie Chart */}
          <svg
            width="180"
            height="180"
            style={{
              display: 'block',
              margin: '0 auto 12px',
              borderRadius: '4px',
            }}
          >
            {renderPieChart(pillarCounts, pillarList, pillarColorMap)}
          </svg>

          {/* Legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {pillarList.map((pillar) => (
              <div
                key={pillar.pillarName}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#333',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: pillarColorMap[pillar.pillarName],
                    flexShrink: 0,
                  }}
                />
                <span>{pillar.pillarName}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{pillarCounts[pillar.pillarName]}</span>
              </div>
            ))}
          </div>

          {/* Status Legend */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                color: '#555',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Goal Status
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {goalStatusesQuery.data?.map((status) => (
                <div
                  key={status.statusId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '11px',
                    color: '#333',
                  }}
                >
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: status.backgroundColor,
                      border: `1px solid ${status.textColor}`,
                      flexShrink: 0,
                    }}
                  />
                  <span>{status.statusName}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Goals Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {isEmptyState ? (
            // Empty State
            <div
              style={{
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '48px 24px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px', color: '#ccc' }}>🔍</div>
              <h2
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#333',
                  marginBottom: '8px',
                }}
              >
                No goals match the selected filters.
              </h2>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                Try adjusting your filters to see results.
              </p>
              <button
                onClick={handleClearFilters}
                style={{
                  border: '1px solid #0078d4',
                  background: '#fff',
                  color: '#0078d4',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            // Goals by Pillar
            pillarList.map((pillar) => (
              <div
                key={pillar.pillarName}
                style={{
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <div
                    style={{
                      width: '5px',
                      height: '32px',
                      borderRadius: '3px',
                      backgroundColor: pillarColorMap[pillar.pillarName],
                      flexShrink: 0,
                    }}
                  />
                  <h3
                    style={{
                      fontSize: '14px',
                      fontWeight: 900,
                      margin: 0,
                      color: '#111',
                    }}
                  >
                    {pillar.pillarName}
                  </h3>
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#888',
                      fontWeight: 400,
                      marginLeft: '4px',
                    }}
                  >
                    ({goalsByPillar[pillar.pillarName]?.length || 0} {(goalsByPillar[pillar.pillarName]?.length || 0) === 1 ? 'goal' : 'goals'})
                  </span>
                </div>

                <div
                  style={{
                    maxHeight: '318px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: '20px',
                  }}
                >
                  {goalsByPillar[pillar.pillarName] && goalsByPillar[pillar.pillarName].length > 0 ? (
                    goalsByPillar[pillar.pillarName].map((goal, index) => (
                      <GoalRow
                        key={goal.goalId}
                        goal={goal}
                        index={index + 1}
                        onEdit={() => handleOpenEditModal(goal)}
                        onStatusChange={(newStatus) =>
                          handleStatusChange(goal.goalId, newStatus)
                        }
                        isStatusDropdownOpen={statusDropdownOpenId === goal.goalId}
                        onStatusDropdownOpen={() => setStatusDropdownOpenId(goal.goalId)}
                        onStatusDropdownClose={() => setStatusDropdownOpenId(null)}
                        statusColorMap={statusColorMap}
                        statusTextColorMap={statusTextColorMap}
                        goalStatuses={goalStatusesQuery.data || []}
                      />
                    ))
                  ) : (
                    <div
                      style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '12px',
                      }}
                    >
                      No goals in this pillar
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Goal Modal */}
      {showAddModal && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.45)',
              zIndex: 9999,
            }}
            onClick={() => setShowAddModal(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#fff',
              borderRadius: '8px',
              width: '500px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
              zIndex: 10000,
            }}
          >
            <AddGoalModal
              formFields={addFormFields}
              availableBrgs={availableBrgs}
              isLeader={isLeader}
              pillarList={pillarList}
              fiscalYearList={fiscalYearList}
              onFieldChange={handleAddFieldChange}
              onAddAnother={handleAddAnother}
              onRemoveField={handleRemoveField}
              onSave={handleAddGoals}
              onCancel={() => setShowAddModal(false)}
              isLoading={goalsMutations.createMultiple.isPending}
            />
          </div>
        </>
      )}

      {/* Edit Goal Modal */}
      {showEditModal && editingGoal && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.45)',
              zIndex: 9999,
            }}
            onClick={() => {
              setShowEditModal(false);
              setShowDeleteConfirm(false);
            }}
          />
          {!showDeleteConfirm ? (
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: '8px',
                width: '500px',
                maxWidth: '95vw',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
                zIndex: 10000,
              }}
            >
              <EditGoalModal
                goal={editingGoal}
                availableBrgs={availableBrgs}
                isLeader={isLeader}
                pillarList={pillarList}
                fiscalYearList={fiscalYearList}
                onFieldChange={handleEditFieldChange}
                onSave={handleUpdateGoal}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingGoal(null);
                }}
                onDelete={() => setShowDeleteConfirm(true)}
                isLoading={goalsMutations.update.isPending}
                goalStatuses={goalStatusesQuery.data || []}
              />
            </div>
          ) : (
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: '8px',
                width: '400px',
                maxWidth: '95vw',
                boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
                zIndex: 10001,
              }}
            >
              <DeleteConfirmModal
                onConfirm={handleDeleteGoal}
                onCancel={() => setShowDeleteConfirm(false)}
                isLoading={goalsMutations.delete.isPending}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Helper Components ───────────────────────────────────────────────────────

function renderPieChart(
  counts: Record<string, number>,
  pillarList: Array<{ pillarId: number; pillarName: string; colorCode: string; isActive: boolean }>,
  pillarColorMap: Record<string, string>
) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <text x="90" y="100" textAnchor="middle" fill="#ccc" fontSize="12">
        No data
      </text>
    );
  }

  const cx = 90,
    cy = 90,
    r = 70,
    start = -Math.PI / 2;
  const slices: JSX.Element[] = [];
  let currentAngle = start;

  pillarList.forEach((pillar) => {
    const count = counts[pillar.pillarName];
    if (count === 0) {
      return; // Skip pillars with no goals
    }

    const sliceAngle = (count / total) * 2 * Math.PI;
    const endAngle = currentAngle + sliceAngle;

    const x1 = cx + r * Math.cos(currentAngle);
    const y1 = cy + r * Math.sin(currentAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);

    // For a full circle (or near-full), we need to draw it as two semi-circles
    // to avoid SVG arc issues
    if (sliceAngle >= 2 * Math.PI - 0.01) {
      // Full circle: draw two semi-circles
      const midAngle = currentAngle + Math.PI;
      const xMid = cx + r * Math.cos(midAngle);
      const yMid = cy + r * Math.sin(midAngle);

      slices.push(
        <path
          key={`${pillar.pillarName}-1`}
          d={[
            `M ${cx} ${cy}`,
            `L ${x1} ${y1}`,
            `A ${r} ${r} 0 1 1 ${xMid} ${yMid}`,
            'Z',
          ].join(' ')}
          fill={pillarColorMap[pillar.pillarName]}
          stroke="white"
          strokeWidth="2"
        />
      );

      slices.push(
        <path
          key={`${pillar.pillarName}-2`}
          d={[
            `M ${cx} ${cy}`,
            `L ${xMid} ${yMid}`,
            `A ${r} ${r} 0 1 1 ${x1} ${y1}`,
            'Z',
          ].join(' ')}
          fill={pillarColorMap[pillar.pillarName]}
          stroke="white"
          strokeWidth="2"
        />
      );
    } else {
      const largeArc = sliceAngle > Math.PI ? 1 : 0;

      const pathData = [
        `M ${cx} ${cy}`,
        `L ${x1} ${y1}`,
        `A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ');

      slices.push(
        <path
          key={pillar.pillarName}
          d={pathData}
          fill={pillarColorMap[pillar.pillarName]}
          stroke="white"
          strokeWidth="2"
        />
      );
    }

    currentAngle = endAngle;
  });

  return slices;
}

interface GoalRowProps {
  goal: BrgGoalDto;
  index: number;
  onEdit: () => void;
  onStatusChange: (newStatus: GoalStatus) => void;
  isStatusDropdownOpen: boolean;
  onStatusDropdownOpen: () => void;
  onStatusDropdownClose: () => void;
  statusColorMap: Record<string, string>;
  statusTextColorMap: Record<string, string>;
  goalStatuses: any[];
}

function GoalRow({
  goal,
  index,
  onEdit,
  onStatusChange,
  isStatusDropdownOpen,
  onStatusDropdownOpen,
  onStatusDropdownClose,
  statusColorMap,
  statusTextColorMap,
  goalStatuses,
}: GoalRowProps) {
  const lastAudit = goal.statusAuditTrail && goal.statusAuditTrail[goal.statusAuditTrail.length - 1];
  // Check if the goal has been updated (either status or other fields)
  const hasBeenModified = lastAudit || (goal.updatedAt && goal.updatedAt !== goal.createdAt);
  const lastUpdatedText = lastAudit
    ? `Last updated by ${lastAudit.updatedByName} on ${new Date(lastAudit.updatedAt).toLocaleDateString()}`
    : hasBeenModified && goal.updatedAt && (goal as any).updatedByName
      ? `Last updated by ${(goal as any).updatedByName} on ${new Date(goal.updatedAt).toLocaleDateString()}`
      : `Created by ${goal.createdByName} on ${new Date(goal.createdAt).toLocaleDateString()}`;

  return (
    <div
      onClick={(e) => {
        // Don't open edit modal if clicking on status badge
        if (!(e.target as HTMLElement).closest('.status-badge')) {
          onEdit();
        }
      }}
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        cursor: 'pointer',
        transition: 'background 0.15s',
        background: 'transparent',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = '#f7f9ff';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 800,
          color: '#888',
          width: '28px',
          flexShrink: 0,
          marginTop: '1px',
        }}
      >
        G{index}
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '12px', color: '#222', fontWeight: 600, marginBottom: '4px' }}>
          {goal.goalText}
        </div>
        <div style={{ fontSize: '10px', color: '#999', marginBottom: '6px' }}>
          {goal.fiscalYear} · {goal.brgName}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#555',
            marginBottom: '3px',
            paddingLeft: '10px',
            borderLeft: '2px solid #ddd',
          }}
        >
          <strong>KR:</strong> {goal.keyResultText}
        </div>
      </div>

      <div style={{ flexShrink: 0, position: 'relative' }}>
        <div
          className="status-badge"
          onClick={(e) => {
            e.stopPropagation();
            if (isStatusDropdownOpen) {
              onStatusDropdownClose();
            } else {
              onStatusDropdownOpen();
            }
          }}
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            position: 'relative',
            userSelect: 'none',
            backgroundColor: statusColorMap[goal.status],
            color: statusTextColorMap[goal.status],
            border: `1px solid ${statusTextColorMap[goal.status]}`,
            opacity: 0.9,
          }}
          onMouseEnter={(e) => {
            if (lastUpdatedText) {
              const badge = e.currentTarget as HTMLElement;
              const badgeRect = badge.getBoundingClientRect();
              
              const tooltip = document.createElement('div');
              tooltip.textContent = lastUpdatedText;
              tooltip.style.position = 'fixed';
              tooltip.style.top = (badgeRect.bottom + 8) + 'px';
              tooltip.style.left = (badgeRect.left + badgeRect.width / 2) + 'px';
              tooltip.style.transform = 'translateX(-50%)';
              tooltip.style.background = '#333';
              tooltip.style.color = '#fff';
              tooltip.style.padding = '6px 10px';
              tooltip.style.borderRadius = '4px';
              tooltip.style.fontSize = '11px';
              tooltip.style.whiteSpace = 'nowrap';
              tooltip.style.zIndex = '10001';
              tooltip.style.pointerEvents = 'none';
              
              document.body.appendChild(tooltip);
              
              // Store tooltip on element for cleanup
              (badge as any).__tooltip = tooltip;
            }
          }}
          onMouseLeave={(e) => {
            const tooltip = (e.currentTarget as any).__tooltip;
            if (tooltip) {
              tooltip.remove();
              delete (e.currentTarget as any).__tooltip;
            }
          }}
        >
          {goal.status} ▾
        </div>

        {/* Status Dropdown */}
        {isStatusDropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '6px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              zIndex: 500,
              minWidth: '170px',
              overflow: 'hidden',
            }}
          >
            {goalStatuses?.map((status) => (
                <div
                  key={status.statusId}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(status.statusName as GoalStatus);
                  }}
                  style={{
                    padding: '8px 14px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: status.statusName === goal.status ? '#f4f6ff' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = '#f4f6ff';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      status.statusName === goal.status ? '#f4f6ff' : 'transparent';
                  }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: status.backgroundColor,
                      border: `1px solid ${status.textColor}`,
                      flexShrink: 0,
                    }}
                  />
                  {status.statusName}
                  {status.statusName === goal.status && (
                    <span style={{ marginLeft: 'auto', fontSize: '12px' }}>✓</span>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface AddGoalModalProps {
  formFields: AddGoalFormState[];
  availableBrgs: any[];
  isLeader: boolean;
  pillarList: Array<{ pillarId: number; pillarName: string; colorCode: string; isActive: boolean }>;
  fiscalYearList: FiscalYearDto[];
  onFieldChange: (index: number, field: keyof AddGoalFormState, value: string) => void;
  onAddAnother: () => void;
  onRemoveField: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function AddGoalModal({
  formFields,
  availableBrgs,
  isLeader,
  pillarList,
  fiscalYearList,
  onFieldChange,
  onAddAnother,
  onRemoveField,
  onSave,
  onCancel,
  isLoading,
}: AddGoalModalProps) {
  // Validate that all required fields are filled
  const isFormValid = formFields.every((field) => {
    return (
      field.brgId.trim() !== '' &&
      field.pillar !== '' &&
      field.goalText.trim() !== '' &&
      field.keyResultText.trim() !== ''
    );
  });

  return (
    <>
      <div
        style={{
          background: '#1a3bcc',
          color: '#fff',
          padding: '14px 18px',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
        }}
      >
        <h2 style={{ fontSize: '14px', fontWeight: 800, margin: 0 }}>Add Goal / Key Result</h2>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
            fontWeight: 700,
            padding: 0,
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: '20px 24px' }}>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '18px', lineHeight: '1.5' }}>
          Please define at least one goal your BRG intends to achieve, along with a key result
          that will help measure success.
        </div>

        {formFields.map((field, index) => (
          <GoalFormSection
            key={index}
            index={index}
            field={field}
            availableBrgs={availableBrgs}
            isLeader={isLeader}
            pillarList={pillarList}
            fiscalYearList={fiscalYearList}
            onFieldChange={(f, v) => onFieldChange(index, f, v)}
            onRemove={() => onRemoveField(index)}
            showRemove={formFields.length > 1}
          />
        ))}

        <button
          onClick={onAddAnother}
          style={{
            fontSize: '11px',
            color: '#1a3bcc',
            fontWeight: 700,
            cursor: 'pointer',
            textAlign: 'right',
            display: 'block',
            marginTop: '6px',
            background: 'none',
            border: 'none',
            padding: 0,
            width: '100%',
          }}
        >
          + Add Another Goal / Key Result Pair
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '14px 24px 20px',
          borderTop: '1px solid #eee',
        }}
      >
        <button
          onClick={onCancel}
          style={{
            background: '#fff',
            color: '#444',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={isLoading || !isFormValid}
          style={{
            background: isLoading || !isFormValid ? '#ccc' : '#1a3bcc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 24px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: isLoading || !isFormValid ? 'not-allowed' : 'pointer',
            opacity: isLoading || !isFormValid ? 0.7 : 1,
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </>
  );
}

interface GoalFormSectionProps {
  index: number;
  field: AddGoalFormState;
  availableBrgs: any[];
  isLeader: boolean;
  pillarList: Array<{ pillarId: number; pillarName: string; colorCode: string; isActive: boolean }>;
  fiscalYearList: FiscalYearDto[];
  onFieldChange: (field: keyof AddGoalFormState, value: string) => void;
  onRemove: () => void;
  showRemove: boolean;
}

function GoalFormSection({
  index,
  field,
  availableBrgs,
  isLeader,
  pillarList,
  fiscalYearList,
  onFieldChange,
  onRemove,
  showRemove,
}: GoalFormSectionProps) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#222', marginBottom: '12px' }}>
        Goal #{index + 1}
      </div>

      {/* Three-column row: BRG, Fiscal Year, Pillar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            BRG <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <select
            value={field.brgId}
            onChange={(e) => onFieldChange('brgId', e.target.value)}
            disabled={isLeader && availableBrgs.length === 1}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              backgroundColor: '#fff',
            }}
          >
            <option value="">Select BRG</option>
            {availableBrgs.map((brg) => (
              <option key={brg.brgId} value={brg.brgId.toString()}>
                {brg.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 0.8 }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Fiscal Year <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <select
            value={field.fiscalYear}
            onChange={(e) => onFieldChange('fiscalYear', e.target.value)}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              backgroundColor: '#fff',
            }}
          >
            {fiscalYearList.map((fy) => (
              <option key={fy.fiscalYearId} value={fy.fiscalYearCode}>
                {fy.fiscalYearCode}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Pillar <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <select
            value={field.pillar}
            onChange={(e) => onFieldChange('pillar', e.target.value as (GoalPillar | ''))}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              backgroundColor: '#fff',
            }}
          >
            <option value="">Select Pillar</option>
            {pillarList.map((pillar) => (
              <option key={pillar.pillarName} value={pillar.pillarName}>
                {pillar.pillarName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Goal Description */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
          Goal Description <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <textarea
          placeholder="Enter your BRG's goal — what do you hope to accomplish?"
          value={field.goalText}
          onChange={(e) => onFieldChange('goalText', e.target.value)}
          style={{
            width: '100%',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '7px 10px',
            fontSize: '11px',
            fontFamily: 'Arial, sans-serif',
            minHeight: '60px',
            resize: 'vertical',
            color: '#666',
          }}
        />
      </div>

      {/* Key Result */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
          Key Result #1 (tied to Goal #{index + 1}) <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <div style={{ fontSize: '9px', color: '#888', marginBottom: '4px', fontStyle: 'italic' }}>
          Example: Host 3 leadership development workshops by Q4.
        </div>
        <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
          Key Result <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <textarea
          placeholder="Enter a measurable result for this goal — how will you track success?"
          value={field.keyResultText}
          onChange={(e) => onFieldChange('keyResultText', e.target.value)}
          style={{
            width: '100%',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '7px 10px',
            fontSize: '11px',
            fontFamily: 'Arial, sans-serif',
            minHeight: '60px',
            resize: 'vertical',
          }}
        />
      </div>

      {showRemove && (
        <button
          onClick={onRemove}
          style={{
            background: '#fff',
            color: '#c62828',
            border: '1px solid #c62828',
            borderRadius: '4px',
            padding: '6px 12px',
            fontSize: '11px',
            fontWeight: 700,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Remove This Pair
        </button>
      )}
    </div>
  );
}

interface EditGoalModalProps {
  goal: EditGoalFormState;
  availableBrgs: any[];
  isLeader: boolean;
  pillarList: Array<{ pillarId: number; pillarName: string; colorCode: string; isActive: boolean }>;
  fiscalYearList: FiscalYearDto[];
  onFieldChange: (field: keyof EditGoalFormState, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isLoading: boolean;
  goalStatuses: any[];
}

function EditGoalModal({
  goal,
  availableBrgs,
  isLeader,
  pillarList,
  fiscalYearList,
  onFieldChange,
  onSave,
  onCancel,
  onDelete,
  isLoading,
  goalStatuses,
}: EditGoalModalProps) {
  // Validate that all required fields are filled
  const isFormValid =
    goal.brgId.trim() !== '' &&
    goal.goalText.trim() !== '' &&
    goal.keyResultText.trim() !== '';

  return (
    <>
      <div
        style={{
          background: '#1a3bcc',
          color: '#fff',
          padding: '14px 18px',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
        }}
      >
        <h2 style={{ fontSize: '14px', fontWeight: 800, margin: 0 }}>Edit Goal / Key Result</h2>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
            fontWeight: 700,
            padding: 0,
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {/* Three-column row: BRG, Fiscal Year, Pillar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
              BRG <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={goal.brgId}
              onChange={(e) => onFieldChange('brgId', e.target.value)}
              disabled={isLeader}
              style={{
                width: '100%',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '6px 8px',
                fontSize: '11px',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#fff',
              }}
            >
              {availableBrgs.map((brg) => (
                <option key={brg.brgId} value={brg.brgId.toString()}>
                  {brg.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: 0.8 }}>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
              Fiscal Year <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={goal.fiscalYear}
              onChange={(e) => onFieldChange('fiscalYear', e.target.value)}
              style={{
                width: '100%',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '6px 8px',
                fontSize: '11px',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#fff',
              }}
            >
              {fiscalYearList.map((fy) => (
                <option key={fy.fiscalYearId} value={fy.fiscalYearCode}>
                  {fy.fiscalYearCode}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
              Pillar <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={goal.pillar}
              onChange={(e) => onFieldChange('pillar', e.target.value as GoalPillar)}
              style={{
                width: '100%',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '6px 8px',
                fontSize: '11px',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#fff',
              }}
            >
              {pillarList.map((pillar) => (
                <option key={pillar.pillarName} value={pillar.pillarName}>
                  {pillar.pillarName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Status <span style={{ color: '#888', fontStyle: 'italic', fontWeight: 400 }}>(Admin only)</span>
          </label>
          <select
            value={goal.status}
            onChange={(e) => onFieldChange('status', e.target.value as GoalStatus)}
            disabled={isLeader}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              backgroundColor: '#fff',
            }}
          >
            {goalStatuses.map((status) => (
              <option key={status.statusId} value={status.statusName}>
                {status.statusName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Goal Description <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <textarea
            value={goal.goalText}
            onChange={(e) => onFieldChange('goalText', e.target.value)}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '7px 10px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              minHeight: '60px',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Key Result #1 (tied to Goal #1) <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <div style={{ fontSize: '9px', color: '#888', marginBottom: '4px', fontStyle: 'italic' }}>
            Example: Host 2 leadership development workshops by Q4
          </div>
          <label style={{ fontSize: '10px', fontWeight: 700, color: '#666', display: 'block', marginBottom: '4px' }}>
            Key Result <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <textarea
            value={goal.keyResultText}
            onChange={(e) => onFieldChange('keyResultText', e.target.value)}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '7px 10px',
              fontSize: '11px',
              fontFamily: 'Arial, sans-serif',
              minHeight: '60px',
              resize: 'vertical',
            }}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '14px 24px 20px',
          borderTop: '1px solid #eee',
        }}
      >
        <button
          onClick={onDelete}
          style={{
            background: '#fff',
            color: '#c62828',
            border: '1px solid #c62828',
            borderRadius: '4px',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Delete Goal
        </button>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{
              background: '#fff',
              color: '#444',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isLoading || !isFormValid}
            style={{
              background: isLoading || !isFormValid ? '#ccc' : '#1a3bcc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 24px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: isLoading || !isFormValid ? 'not-allowed' : 'pointer',
              opacity: isLoading || !isFormValid ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
}

interface DeleteConfirmModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteConfirmModal({ onConfirm, onCancel, isLoading }: DeleteConfirmModalProps) {
  return (
    <div style={{ padding: '24px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#111', marginBottom: '10px' }}>
        Delete Goal
      </h3>
      <p style={{ fontSize: '12px', color: '#555', lineHeight: '1.5', marginBottom: '18px' }}>
        Are you sure you want to delete this goal? This action cannot be undone.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          onClick={onCancel}
          style={{
            background: '#fff',
            color: '#444',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          style={{
            background: '#c62828',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 24px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  );
}
